import { Injectable, Injector } from '@angular/core';
import { AppVersion } from '@awesome-cordova-plugins/app-version/ngx';
import { FirebaseX } from '@awesome-cordova-plugins/firebase-x/ngx';
import { Intercom } from '@awesome-cordova-plugins/intercom/ngx';
import { Smartlook, SmartlookUserIdentifier } from '@ionic-native/smartlook/ngx';
import { Platform } from '@ionic/angular';
import { Storage } from '@ionic/storage';
import axios from 'axios';
import firebase from 'firebase/app';
import 'firebase/auth';
import { Observable, BehaviorSubject } from 'rxjs';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/take';
import { first } from 'rxjs/operators';

import { HttpHeaders } from '@angular/common/http';
import { IPortfolio } from '@interfaces/portfolios';
import { IUserQuestionnaire } from '@interfaces/user-questionnaire';
import { IUser } from '@interfaces/users';
import { ThemesService } from '@services/themes/themes.service';
import { environment } from 'src/environments/environment';
import { AccountManagerService } from '../account-manager/account-manager.service';
import { DrivewealthService } from '../drivewealth/drivewealth.service';
import { ErrorAndLoadingService } from '../error-loading/error-loading.service';
import { FirebaseService } from '../firebase/firebase.service';
import { GoalService } from '../goal/goal.service';
import { MovementsService } from '../movement/movement.service';
import { PortfolioService } from '../portfolio/portfolio.service';
import { RecurrentBuysService } from '../recurrent-buys/recurrent-buys.service';
import { UserService } from '../user/user.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  user: Observable<IUser>;
  userInfo: IUser;
  initialPortfolio: IPortfolio;
  redirectPage: string;
  isNavigatingAwayFromLogin: boolean;
  userLoggedOut: boolean;
  sessionUpdateCallback: (loggedIn: boolean) => void;
  attemptedSignUp: boolean;
  userLastUSDAssetsString: string;
  private assetsService: any;

  private readonly NOTIFY_LOGIN_COOLDOWN_MS: number = 3000;
  private isNotifyLoginPending = false;

  private userSource = new BehaviorSubject<IUser>(null);
  currentUserSource = this.userSource.asObservable();

  private userEmailVerified = new BehaviorSubject<boolean>(null);
  currentUserEmailVerified = this.userEmailVerified.asObservable();

  constructor(
    private appVersion: AppVersion,
    private firebaseService: FirebaseService,
    private userService: UserService,
    private errorService: ErrorAndLoadingService,
    private storage: Storage,
    private portfolioService: PortfolioService,
    private themeService: ThemesService,
    private goalsService: GoalService,
    private movementsService: MovementsService,
    private drivewealthService: DrivewealthService,
    private firebaseNative: FirebaseX,
    private platform: Platform,
    private accountManager: AccountManagerService,
    private intercom: Intercom,
    private recurrentBuysService: RecurrentBuysService,
    private smartlook: Smartlook,
    private injector: Injector,
  ) {
    // tslint:disable-next-line: deprecation
    this.user = Observable.create(obs => {
      return firebase.auth().onAuthStateChanged(
        async (user) => {
          let userData = null;
          if (user) {
            userData = await this.userService.getFullUser(user.uid);
            if (userData) {
              userData.emailVerified = user.emailVerified;
            }
            this.userInfo = userData;
          }
          obs.next(userData);
        },
        err => obs.error(err),
        () => obs.complete());
    });
    this.currentUserSource.subscribe((user) => {
      if (user && user.drivewealthSelfDirectedAccountNumber) {
        if (user.lastHistoricEvolution && user.lastHistoricEvolution.effectivePortfolioComposition) {
          const usdAssetsUser = user.lastHistoricEvolution.effectivePortfolioComposition.filter((asset) => asset.amountUSD);
          const userCurrentUSDAssetString = JSON.stringify(usdAssetsUser);
          if (userCurrentUSDAssetString !== this.userLastUSDAssetsString) {
            this.userLastUSDAssetsString = userCurrentUSDAssetString;
            this.drivewealthService.getDrivewealthAccountSummary()
            .catch(() => { return; });
          }
        }
      } else {
        this.userLastUSDAssetsString = null;
        this.drivewealthService.deleteLocalDrivewealthAccountSummary();
      }
    });
  }

  private async getAssetsService() {
    if (!this.assetsService) {
      const module = await import('@services/assets/assets.service');
      this.assetsService = this.injector.get(module.AssetsService);
    }
    return this.assetsService;
  }

  signUp(email: string, password: string, nickName: string, save = true, permanentLogin = false) {
    return firebase.auth().createUserWithEmailAndPassword(email, password)
    .then(async (credential) => {
      const user = {
        email: email,
        externalAccountCreated: false,
        externalUserCreated: false,
        hasDeposited: false,
        infoComplete: false,
        personalInfo: {
          nickName: nickName,
        },
        preferences: {
          permanentLogin: permanentLogin,
        },
        requireInfo: true,
        uid: credential.user.uid,
      };
      const partner = environment.partner;
      if (partner) {
        user['partner'] = partner;
      }
      this.firebaseService.setUserId(user.uid);
      this.userInfo = user as IUser;
      this.attemptedSignUp = true;
      this.setLocalUserInfo(nickName, email);
      return this.addUserToFirebase();
    })
    .catch(async (err) => {
      this.firebaseService.logError(err.message);
      const userInDB = await this.addUserToFirebase().catch(() => Promise.resolve());
      if (userInDB) {
        return userInDB;
      }
      const errorMessage = err.code === 'auth/email-already-in-use' ?
        'El email ya se encuentra registrado.' : 'Error en la aplicación, inténtalo nuevamente';
      this.errorService.presentToastWithDuration(2000, 'top', errorMessage, true);
      throw errorMessage;
    });
  }

  async addUserToFirebase() {
    const batch = this.firebaseService.firestore.batch();
    const time = this.firebaseService.timestamp;
    return this.firebaseService.firestore.doc(`/users/${this.userInfo.uid}`).set(
      { ...this.userInfo, createdAt: time, updatedAt: time }, { merge: true },
    ).then(() => this.initUser(this.userInfo))
    .catch(err => {
      this.firebaseService.logError(err.message);
      throw err;
    });
  }

  initUser = (user: IUser) => {
    this.intercom.registerIdentifiedUser({email: user.email, name: user.personalInfo.nickName, userId: user.uid});
    const nickName = user.personalInfo.nickName;
    const capNickName = nickName.charAt(0).toUpperCase() + nickName.slice(1);
    this.intercom.updateUser({
      name: capNickName,
      custom_attributes: {
        info_complete: false,
        has_deposited: false,
        nick_name: capNickName,
      },
    });
    this.firebaseService.setCrashlyticsUserId(user.uid);
    this.firebaseService.logEvent('sign_up', {uid: user.uid});
    this.updateUserToken(user.uid);
    this.getObservables(user);
    this.sessionUpdateCallback(true);
    this.updateUserPlatformAndVersion(user.uid);
    return user;
  }

  async setLocalUserInfo(nickName: string, email: string) {
    try {
      await this.storage.set('name', nickName);
      await this.storage.set('email', email);
    } catch (err) {
      this.firebaseService.logError(err);
    }
  }

  async updateUserToken(uid: string) {
    if (this.platform.is('cordova')) {
      const deviceRef = this.firebaseService.getCollection('devices');
      const token = await this.firebaseNative.getToken();
      this.firebaseService.updateDocumentWithMerge({ token, userId: uid }, deviceRef, token);
    }
  }

  async getUserToken() {
    const currentUser = firebase.auth().currentUser;
    if (currentUser) {
      const token = await currentUser.getIdToken();
      return token;
    } else {
      const err = new Error('Error getting auth token');
      this.firebaseService.logError(err.message);
      throw err;
    }
  }

  async updateUserPlatformAndVersion(uid: string) {
    const appVersion = '1.29.20';
    if (appVersion !== this.userInfo.appVersion) {
      const usersRef = this.firebaseService.getCollection('users');
      const userNewInfo = {
        appVersion,
        appPlatforms: this.platform.platforms(),
        appLastOpened: new Date(),
      };
      this.firebaseService.updateDocument(userNewInfo, usersRef, uid);
    }
  }

  async signIn(email: string, password: string, permanentLogin?: boolean, token?: string, otpCode?: string, device?: any) {
    const loginMethod = token ? 'token' : 'password';
    this.firebaseService.logEvent('login_try', {login_method: loginMethod});
    if (token) {
      firebase.auth().setPersistence(firebase.auth.Auth.Persistence.SESSION);
    }
    const login = token ? firebase.auth().signInWithCustomToken(token) :
                          firebase.auth().signInWithEmailAndPassword(email, password);
    return login.then(async (user) => {
      try {
        if (!token && otpCode && email) {
          await this.firebaseService.functions
            .httpsCallable('authMfaVerify')({ email, code: otpCode, device, validate: true });
        }
      } catch (e) {
        // If OTP verification fails, logout and propagate error
        await firebase.auth().signOut();
        throw e;
      }

        // Block login if user has device security enabled (only available in Racional 2.0)
        if (user) {
          const hasDeviceSecurity = await this.hasDeviceSecurityEnabled();
          if (hasDeviceSecurity) {
            await firebase.auth().signOut();
            this.errorService.presentToastWithDuration(
              5000,
              'top',
              'Tu cuenta tiene seguridad de dispositivo activada. Inicia sesión en la nueva app de Racional.',
              true,
            );
            return null;
          }
        }

        if (user) {
          this.userService.callUpdateUser();
          this.accountManager.addAccount(email, password);
          this.updateUserToken(user.user.uid);
          this.firebaseService.setUserId(user.user.uid);
          this.firebaseService.logEvent('login', {uid: user.user.uid});
          this.firebaseService.setCrashlyticsUserId(user.user.uid);
          this.userInfo = await this.userService.getFullUser(user.user.uid) as IUser;
          if (permanentLogin) {
            const oldValue = this.userInfo.preferences && this.userInfo.preferences.permanentLogin;
            this.userService.updateUserField('preferences.permanentLogin', permanentLogin, oldValue);
          }
          this.updateUserPlatformAndVersion(user.user.uid);
          this.setLocalUserInfo(this.userInfo.personalInfo.nickName, this.userInfo.email);
          const lastHistoricEvolution = this.userInfo.lastHistoricEvolution;
          const totalAmount = lastHistoricEvolution ? Math.round(lastHistoricEvolution.totalAmount) : 0;
          const totalAmountString = totalAmount.toString();
          const portfolioIndex = lastHistoricEvolution ? Math.round(lastHistoricEvolution.portfolioIndex * 100) / 100 : 100;
          const portfolioIndexString = portfolioIndex.toString();
          const allTimeProfitAmount = lastHistoricEvolution ? Math.round(lastHistoricEvolution.allTimeProfitAmount) : 0;
          const lastDepositDateUnix = this.userInfo.lastDeposit ? Math.trunc(this.userInfo.lastDeposit.toDate().getTime() / 1000) : null;
          this.firebaseService.setUserProperty('info_complete', this.userInfo.infoComplete ? 'true' : 'false');
          this.firebaseService.setUserProperty('invested_amount', totalAmountString);
          this.firebaseService.setUserProperty('portfolio_index', portfolioIndexString);
          this.intercom.registerIdentifiedUser({email: this.userInfo.email, userId: user.user.uid});
          this.intercom.updateUser({
            custom_attributes: {
              invested_amount: totalAmount,
              portfolio_index: portfolioIndex,
              profit_amount: allTimeProfitAmount,
              last_deposit: lastDepositDateUnix,
              has_deposited: this.userInfo.hasDeposited,
            },
          });
          this.getObservables(user.user);
          this.updateUserPlatformAndVersion(user.user.uid);
        }
        this.sessionUpdateCallback(true);
        // Fire-and-forget: notify successful login to trigger security email if needed
        if (this.isNotifyLoginPending) {
          return; // another call is already queued/running
        }

        this.isNotifyLoginPending = true;
        setTimeout(
          () => (this.isNotifyLoginPending = false),
          this.NOTIFY_LOGIN_COOLDOWN_MS,
        );

        try {
          const deviceId: string | null = this.platform.is('cordova')
            ? await this.firebaseNative.getToken()
            : null;

          await this.firebaseService.functions
            .httpsCallable('NotifySuccessfulLogin')({ deviceId });
        } catch (e) {
          // Non-blocking: ignore errors
        }
        return user;
      })
      .catch((err) => {
        this.firebaseService.logError(err.message);
        if (err.code === 'auth/user-not-found') {
          this.errorService.presentToastWithDuration(2000, 'top', 'Correo no registrado o contraseña incorrecta.', true);
        } else if (err.code === 'auth/wrong-password') {
          this.errorService.presentToastWithDuration(2000, 'top', 'Correo no registrado o contraseña incorrecta.', true);
        }
        return null;
      });
  }

  signOut() {
    try {
      this.intercom.logout();
      this.destroyObservables();
    } catch { }
    return firebase.auth().signOut().then((val) => {
      this.userInfo = null;
      this.userSource.next(null);
      this.sessionUpdateCallback(false);
    }).catch((err) => {
      this.firebaseService.logError(err.message);
      this.errorService.presentToastWithDuration(2000, 'top', 'Error en la aplicación, inténtalo nuevamente', true);
    });
  }

  signOutCompletely() {
    this.signOut();
    this.storage.clear();
    this.accountManager.removeAccount();
  }

  async isLoggedIn(subscribe = true) {
    const loggedIn = await this.user.pipe(first()).toPromise().then((user) => {
      this.userInfo = user;
      if (user && subscribe) {
        this.getObservables(user);
        this.updateUserPlatformAndVersion(user.uid);
      }
      this.sessionUpdateCallback(!(user === null));
      return !(user === null);
    });
    return loggedIn;
  }

  async waitForLoggedUser() {
    if (this.userSource.value) { return true; }
    const loggedIn = await this.user.pipe(first()).toPromise().then(async (user) => {
      this.sessionUpdateCallback(!(user === null));
      if (user) {
        this.getObservables(user);
        this.updateUserPlatformAndVersion(user.uid);
        return new Promise(resolve => {
          Observable.combineLatest([
            this.currentUserSource,
            this.portfolioService.currentPortfoliosSource,
           ]).subscribe(([userInfo, portfolios]: [IUser, IPortfolio[]]) => {
             if (userInfo && portfolios) { resolve(true); }
           });
        });
      } else {
        return false;
      }
    });
    return loggedIn;
  }

  /**
   * Reset the user's password and call killSessions.
   * @param oodCode The out-of-band code for password reset.
   * @param newPassword The new password to set.
   * @returns Promise<void>
   */
  async resetPassword(oodCode: string, newPassword: string): Promise<void> {
    try {
      const email = await firebase.auth().verifyPasswordResetCode(oodCode);

      await firebase.auth().confirmPasswordReset(oodCode, newPassword);
      await axios.post(
        `${environment.cloudFunctionsBaseUrl}/KillAuthSessions`,
        { email },
        { headers: this.getHeaders() },
      );
    } catch (err) {
      return Promise.reject(err);
    }
  }

  verifyEmail(actionCode) {
    return firebase.auth().applyActionCode(actionCode).then(() => {
    });
  }

  getObservables(user) {
    this.userService.getFullUserObs(user.uid, this.userSource, user.emailVerified);
    this.portfolioService.getPortfoliosObs(user.uid);
    this.goalsService.getGoalsObs(user.uid);
    this.themeService.getThemesObs();
    this.movementsService.getPendingMovementObs(user.uid);
    this.movementsService.getContributionStrategyObs(user.uid);
    this.movementsService.getUnreadDeposits(user.uid);
    this.movementsService.getDepositsAmountInProgress(user.uid);
    this.movementsService.getPendingOrdersObs(user.uid);
    this.movementsService.getCashFromSellsInTransit(user.uid, this.userInfo.subscription);
    this.movementsService.getDwTrades(user.uid, this.userInfo.subscription);
    this.userService.getOwnSocialProfileObs(this.userInfo.profileId);
    this.movementsService.getDwTrades(user.uid, this.userInfo.subscription);
    this.userEmailVerified.next(user.emailVerified);
    this.getAssetsService().then(service => service.getFavoriteAssets(user.uid));
    this.recurrentBuysService.getRecurrentBuys(user.uid);
    this.firebaseService.logEvent('user_login', { userId: this.userInfo.uid });
    this.identifyUserInSmartlook(this.userInfo);
  }

  destroyObservables() {
    this.userService.userSub();
    this.portfolioService.portfoliosSub();
    this.goalsService.goalsSub();
    this.themeService.themesSub();
    this.movementsService.hasPendingDistributionMovementSub();
    this.movementsService.unreadDepositsSub();
    this.movementsService.hasFirstMovementSub();
    this.movementsService.contributionStrategySub();
    this.userService.socialProfileSub();
    this.movementsService.destroyMovementObservables();
    this.movementsService.pendingOrdersSub();
    this.getAssetsService().then(service => service.deleteFavoriteAssets());
    this.recurrentBuysService.getRecurrentBuys();
    this.firebaseService.logEvent('user_logout', {});
  }

  identifyUserInSmartlook(userInfo) {
    if (environment.production && !environment.isPartnerSite && this.platform.is('cordova')) {
      this.smartlook.setUserIdentifier(new SmartlookUserIdentifier(userInfo.uid, {
        email: userInfo.email,
        name: userInfo.personalInfo ? userInfo.personalInfo.names + ' ' + userInfo.personalInfo.lastNames : 'new-user',
      }));
    }
  }

  async refreshVerifiedEmail() {
    await firebase.auth().currentUser.reload();
    this.userEmailVerified.next(firebase.auth().currentUser ? firebase.auth().currentUser.emailVerified : false);
  }

  setUserInitialInfo(userId: string, value: IUserQuestionnaire) {
    this.firebaseService.setUserProperty('salary_range', value.financialData.salaryRange.toString());
    this.firebaseService.setUserProperty('total_savings_amount', value.financialData.totalSavingsAmount.toString());
    const usersRef = this.firebaseService.getCollection('users');
    const data = {
      financialData: value.financialData,
      riskInfo: value.riskInfo,
      requireInfo: false,
    };
    return this.firebaseService.updateDocumentWithMerge(data, usersRef, userId);
  }

  private async hasDeviceSecurityEnabled(): Promise<boolean> {
    const currentUser = firebase.auth().currentUser;
    if (!currentUser) {
      return false;
    }

    const tokenResult = await currentUser.getIdTokenResult(true);
    return tokenResult.claims.deviceSecurityEnabled === true;
  }

  private getHeaders() {
    try {
      return new HttpHeaders({ 'Content-Type': 'application/json' });
    } catch (error) {
      this.firebaseService.logError(error.message);
      throw error;
    }
  }
}
